import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import ReactDOM from 'react-dom'
import { usePopper } from 'react-popper'

import { useEditor } from '@toasttab/sites-components'
import classnames from 'classnames'
import throttle from 'lodash/throttle'

import {
  DisplayableMenuItemTag,
  MenuFormatConfig,
  MenuSearchMode
} from 'src/apollo/sites'
import { ScreenWidth, useIsMobile } from 'src/shared/js/utils/WindowContext'
import { t } from 'src/translations'

import {
  Modal,
  ModalContent,
  ModalOverlay
} from 'shared/components/common/modal'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { normalizeHtmlId } from 'shared/js/normalizeUtils'

import { useGlobalNav } from 'public/components/default_template/GlobalNavContext'
import {
  ScrollDirection,
  scrollToRef,
  useHorizontalScroll
} from 'public/components/default_template/menu_nav/menuNavScrollUtils'
import { MenuItem } from 'public/components/default_template/menu_section/MenuSection'
import { filterAndSortTags } from 'public/components/default_template/online_ordering/item_tags/MenuItemTags'
import { useMenuSearchContext } from 'public/components/default_template/search'
import {
  ArrowBackIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
  ViewListIcon
} from '@toasttab/buffet-pui-icons'
import NavSearchWrapper from '../search/NavSearchWrapper'
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'

export type RefMenuGroup = {
  name: string
  description?: string | null
  ref: React.RefObject<HTMLDivElement>
  id: string
  guid: string
  items: MenuItem[]
}

export type RefMenu = {
  name: string
  guid: string
  ref: React.RefObject<HTMLDivElement>
  groups: RefMenuGroup[]
  description?: string | null
}

type MenuGroup = {
  name?: string | null
  guid?: string | null
  items?: (MenuItem | Partial<MenuItem> | null)[] | null
}

type Menu = {
  name?: string | null
  id?: string
  groups?: (MenuGroup | null)[] | null
  guid?: string | null
  description?: string | null
}

type HookProps = {
  menus?: Menu[] | null
  menuItems?: MenuItem[] | null
  page: '/order' | '/menu'
}

export const useMenuNav = (props: HookProps) => {
  const { restaurant } = useRestaurant()
  const displayableTags = useMemo(
    () =>
      restaurant.config[props.page === '/order' ? 'ooConfig' : 'menuConfig']
        ?.displayableMenuItemTags as DisplayableMenuItemTag[],
    [restaurant.config, props.page]
  )
  const [selectedMenuIndex, setSelectedMenuIndex] = useState(0)
  const [scrolledMenuIndex, setScrolledMenuIndex] = useState(0)
  const [scrolledGuid, setScrolledGuid] = useState<string>()
  const [selectedGuid, setSelectedGuid] = useState<string>()
  // This is so we can render an item modal for any item with just the guid from the Order Page.  It doesn't
  // matter that we overwrite the map with a different version of the item if there are duplicates across groups,
  // we just need a valid group ID and valid list of itemTags for each item guid.
  const itemGuidsToItemsMap = useMemo(
    () =>
      !props.menuItems
        ? new Map<string, MenuItem>()
        : props.menuItems.reduce((map, item) => {
            if (item.guid) {
              map.set(item.guid, item)
            }
            return map
          }, new Map<string, MenuItem>()),
    [props]
  )

  // Collect all menu item tags to build comprehensive GUID->name mapping
  // Critical for RF-32427: enables name-based matching for duplicate tag records
  const allMenuItemTags = useMemo(
    () =>
      props.menus?.flatMap(
        (menu) =>
          menu.groups?.flatMap(
            (group) =>
              group?.items?.flatMap((item) => item?.itemTags ?? []) ?? []
          ) ?? []
      ) ?? [],
    [props.menus]
  )

  const refMenuPages: RefMenu[] = useMemo(
    () =>
      props.menus
        ? props.menus
            .map((menu) => ({
              name: menu.name as string,
              guid: menu.guid || menu.id || (menu.name as string),
              ref: React.createRef<HTMLDivElement>(),
              description: menu.description,
              groups:
                menu.groups
                  ?.filter((group) => group?.items?.length)
                  .map((group: any) => ({
                    ...group,
                    ref: React.createRef<HTMLDivElement>(),
                    guid: group.guid || group.name,
                    id: group.guid || group.name,
                    items: group.items.map((item: MenuItem) => {
                      if (item.guid && group.guid)
                        itemGuidsToItemsMap.set(item.guid, item)
                      return {
                        ...item,
                        itemTags: filterAndSortTags(
                          item?.itemTags ?? [],
                          displayableTags,
                          allMenuItemTags
                        ),
                        ref: React.createRef<HTMLLIElement>()
                      }
                    })
                  })) || []
            }))
            .filter((menu) => menu.groups.length > 0)
        : [],
    [props.menus, displayableTags, itemGuidsToItemsMap, allMenuItemTags]
  )

  return {
    selectedMenuIndex,
    setSelectedMenuIndex,
    scrolledMenuIndex,
    setScrolledMenuIndex,
    scrolledGuid,
    setScrolledGuid,
    selectedGuid,
    setSelectedGuid,
    refMenuPages,
    itemGuidsToItemsMap
  }
}

const MenuNav = ({
  menus,
  selectedMenuIndex,
  setSelectedMenuIndex,
  selectedGroupGuid,
  setSelectedGroupGuid,
  menuConfig,
  navRef,
  className
}: {
  menus: RefMenu[]
  selectedMenuIndex: number
  setSelectedMenuIndex: (index: number) => void
  selectedGroupGuid?: string
  setSelectedGroupGuid: (guid: string) => void
  menuConfig?: MenuFormatConfig | null
  navRef?: React.RefObject<HTMLDivElement>
  className?: string
}) => {
  const {
    scrollContainerRef,
    showLeftArrow,
    showRightArrow,
    scroll,
    scrollEvent
  } = useHorizontalScroll()
  const { canUseSearch } = useMenuSearchContext()
  const { restaurant } = useRestaurant()
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const isMobile = useIsMobile(ScreenWidth.SMALL)
  const { globalNavHeight, globalNavOpen } = useGlobalNav()
  const { track } = useThrottledTracker()

  const trackMobileMenuNavigation = useCallback(
    (source: 'menu_nav' | 'bottom_drawer') => {
      track('Mobile Menu Nav Interaction', {
        source,
        numOfMenus: menus.length,
        totalMenuGroups: menus
          .map((menu) => menu.groups.length)
          .reduce((a, b) => a + b, 0)
      })
    },
    [menus, track]
  )

  if (menuConfig?.hideMenuNav && menuConfig?.hideSubMenuNav) {
    if (menuConfig.menuSearchMode === MenuSearchMode.Visible) {
      // menus are configured to be hidden but search is still visible. clear out menus so they don't show
      menus = []
    } else {
      // all nav items are configured to be hidden, <TopMenuNav /> shouldn't show at all
      return null
    }
  }

  const showMenusWithGroups =
    !menuConfig?.hideMenuNav &&
    !menuConfig?.hideSubMenuNav &&
    !isMobile &&
    menus.length > 1
  const showOnlyGroups =
    menuConfig?.hideMenuNav ||
    (isMobile && !menuConfig?.hideSubMenuNav) ||
    menus.length === 1

  const hasHero = !!restaurant.config.ooConfig?.heroImage?.src

  // There are 3 cases to account for:
  // 1. There is a menuNav and a subMenuNav, in which case we'll render a nav with dropdowns
  // 2. There is only a menuNav and no subMenuNav, in which case we'll render the menus without dropdowns
  // 3. There is only a subMenuNav and no menuNav, or we're on mobile, in which case we redefine the menus
  //    as the subgroups and render them without dropdowns
  const topLevelMenus: RefMenu[] = showOnlyGroups
    ? menus.flatMap((menu) =>
        menu.groups.map((group) => {
          return {
            ...group,
            groups: []
          } as RefMenu
        })
      )
    : menus

  return (
    <div
      className={classnames('menuNavContainer', className)}
      style={
        globalNavOpen
          ? { position: 'sticky', top: `${globalNavHeight}px` }
          : undefined
      }
    >
      <div className='paddedContentWrapper'>
        <div className={classnames('paddedContent', { withoutHero: !hasHero })}>
          <nav
            ref={navRef}
            aria-label={t('menu-nav.menu-navigation')}
            data-testid='menu-nav'
            className='menuNav'
          >
            {topLevelMenus.length > 0 && (
              <div
                className='topMenuNavMenuSelection'
                data-testid='menu-nav-menu-selection'
              >
                <button
                  className='navControl hidden-md-up'
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  tabIndex={isMobile ? 0 : -1}
                >
                  <ViewListIcon
                    accessibility='semantic'
                    aria-label={t('menu-nav.open-full-menu')}
                  />
                </button>
                <MobileMenuModal
                  menus={menus}
                  isOpen={showMobileMenu}
                  onClose={() => setShowMobileMenu(false)}
                  setSelectedGroupGuid={(guid) => {
                    setSelectedGroupGuid(guid)
                  }}
                  trackMobileMenuInteraction={trackMobileMenuNavigation}
                />
                <div
                  className={classnames('topMenuNavWrapper', {
                    hasSearchInput: canUseSearch
                  })}
                >
                  <button
                    type='button'
                    onClick={scroll(ScrollDirection.Backwards)}
                    className={classnames('arrow leftArrow', {
                      arrowHidden: !showLeftArrow
                    })}
                  >
                    <ChevronLeftIcon aria-label={t('menu-nav.scroll-left')} />
                  </button>
                  <div
                    className='sections'
                    data-testid='menu-nav-sections'
                    ref={scrollContainerRef}
                    onScroll={scrollEvent}
                  >
                    {topLevelMenus.map((menuItem, index) => (
                      <MenuNavItem
                        key={`${menuItem.name}-${menuItem.guid}`}
                        menu={menuItem}
                        selected={
                          showOnlyGroups
                            ? selectedGroupGuid === menuItem.guid
                            : selectedMenuIndex === index
                        }
                        setSelected={() => {
                          showOnlyGroups
                            ? setSelectedGroupGuid(menuItem.guid)
                            : setSelectedMenuIndex(index)
                        }}
                        scrollContainerRef={scrollContainerRef}
                        hasDropdown={showMenusWithGroups}
                        trackMobileMenuInteraction={trackMobileMenuNavigation}
                      />
                    ))}
                  </div>
                  <button
                    type='button'
                    onClick={scroll(ScrollDirection.Forwards)}
                    className={classnames('arrow rightArrow', {
                      arrowHidden: !showRightArrow
                    })}
                  >
                    <ChevronRightIcon aria-label={t('menu-nav.scroll-right')} />
                  </button>
                </div>
              </div>
            )}
            <NavSearchWrapper />
          </nav>
        </div>
      </div>
    </div>
  )
}

export const MenuNavItem = ({
  menu,
  selected,
  setSelected,
  scrollContainerRef,
  hasDropdown,
  trackMobileMenuInteraction
}: {
  menu: RefMenu
  selected: boolean
  setSelected: () => void
  scrollContainerRef: RefObject<HTMLDivElement>
  hasDropdown: boolean
  trackMobileMenuInteraction: (source: 'menu_nav' | 'bottom_drawer') => void
}) => {
  const [referenceElement, setReferenceElement] =
    React.useState<HTMLDivElement | null>(null)
  const [popperElement, setPopperElement] =
    React.useState<HTMLDivElement | null>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [30, 15]
        } /* [skidding, distance], https://popper.js.org/docs/v2/modifiers/offset/ */
      }
    ]
  })
  const { isEditor } = useEditor()
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
  const isMobile = useIsMobile(ScreenWidth.SMALL)

  const isFullyInView = useCallback(() => {
    if (!referenceElement || !scrollContainerRef.current) {
      return
    }
    const refRect = referenceElement.getBoundingClientRect()
    const containerRect = scrollContainerRef.current.getBoundingClientRect()
    return (
      refRect.left >= containerRect.left && refRect.right <= containerRect.right
    )
  }, [referenceElement, scrollContainerRef])

  useEffect(() => {
    const detectOutsideAction = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (
        dropdownIsOpen &&
        !popperElement?.contains(target) &&
        !referenceElement?.contains(target)
      ) {
        setDropdownIsOpen(false)
      }
    }

    const detectSelectionFromVerticalScroll = throttle(() => {
      const menuNavInView =
        (scrollContainerRef.current?.getBoundingClientRect().bottom ?? 0) > 0
      if (selected && referenceElement && menuNavInView && !isFullyInView()) {
        referenceElement.scrollIntoView({ block: 'nearest' })
      }
    }, 500)

    if (!isEditor) {
      document.addEventListener('click', detectOutsideAction)
      document.addEventListener('scroll', detectOutsideAction)
      document.addEventListener('scroll', detectSelectionFromVerticalScroll)
      scrollContainerRef.current?.addEventListener(
        'scroll',
        detectOutsideAction
      )
    }

    return () => {
      document.removeEventListener('click', detectOutsideAction)
      document.removeEventListener('scroll', detectOutsideAction)
      document.removeEventListener('scroll', detectSelectionFromVerticalScroll)
    }
  }, [
    dropdownIsOpen,
    popperElement,
    scrollContainerRef,
    referenceElement,
    selected,
    isEditor,
    isFullyInView
  ])

  function handleMenuClick(
    e: React.KeyboardEvent | React.MouseEvent,
    menu: RefMenu
  ) {
    if (e.type === 'keydown') {
      const keyEvent = e as React.KeyboardEvent
      if (keyEvent.code !== 'Space' && keyEvent.code !== 'Enter') {
        //  if the arrow down key is pressed AND has a dropdown, continue
        if (keyEvent.code === 'ArrowDown') {
          if (!hasDropdown) {
            return
          }
        } else {
          return // only continue if the spacebar or enter key is pressed
        }
      }
      e.stopPropagation()
      e.preventDefault()
    }
    setSelected()
    if (isMobile) {
      trackMobileMenuInteraction('menu_nav')
    }
    if (hasDropdown) {
      if (!isFullyInView()) {
        referenceElement?.scrollIntoView({ block: 'nearest' })
        // Set a timeout so the event listener that automatically closes the dropdown on scroll
        // doesn't fire before we can scroll the menu item into view and open the dropdown
        setTimeout(() => setDropdownIsOpen(!dropdownIsOpen), 50)
      } else {
        setDropdownIsOpen(!dropdownIsOpen)
      }
    } else {
      scrollToRef(menu.ref, isEditor)
      const firstItem = menu.ref.current?.querySelector<HTMLElement>('li a')
      firstItem?.focus({ preventScroll: true })
    }
  }

  useEffect(() => {
    if (dropdownIsOpen) {
      const options = popperElement?.querySelectorAll('[role="menuitem"]')
      if (options && options.length > 0) {
        const elementToFocus = options.item(0) as HTMLElement
        // We have to wait for all of the scroll presses to process before moving focus or else
        // the dropdown will close automatically
        setTimeout(() => elementToFocus.focus(), 50)
      }
    }
  }, [dropdownIsOpen, popperElement])

  const handleTabOnDropdown = useCallback(
    (e: React.KeyboardEvent) => {
      const options = popperElement?.querySelectorAll('[role="menuitem"]')
      const target = e.target as HTMLElement
      if (options && target.id === options[options.length - 1]?.id) {
        setDropdownIsOpen(false)
        e.stopPropagation()
        e.preventDefault()
        setTimeout(() => {
          dropdownRef.current?.focus()
        }, 50)
      }
    },
    [dropdownRef, popperElement]
  )

  const handleArrowKeyOnDropdown = useCallback((e: React.KeyboardEvent) => {
    e.stopPropagation()
    e.preventDefault()
    const target = e.target as HTMLElement
    const prevEl = target.previousElementSibling as HTMLElement
    const nextEl = target.nextElementSibling as HTMLElement
    if (e.code === 'ArrowUp') {
      prevEl && prevEl.focus()
    } else if (e.code === 'ArrowDown') {
      nextEl && nextEl.focus()
    }
  }, [])

  const selectGroup = useCallback(
    (group: RefMenuGroup) => {
      scrollToRef(group.ref, isEditor)
      const firstItem = group.ref.current?.querySelector<HTMLElement>('li a')
      firstItem?.focus({ preventScroll: true })
      setDropdownIsOpen(false)
    },
    [isEditor, setDropdownIsOpen]
  )

  function handleSubMenuItemKeyDown(
    e: React.KeyboardEvent,
    group: RefMenuGroup
  ) {
    if (e.code === 'Tab') {
      handleTabOnDropdown(e)
    }
    if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
      handleArrowKeyOnDropdown(e)
    }
    if (e.code !== 'Escape' && e.code !== 'Space' && e.code !== 'Enter') {
      return
    }
    e.stopPropagation()
    e.preventDefault()
    if (e.code === 'Escape') {
      dropdownRef.current?.focus()
      setDropdownIsOpen(false)
    } else {
      selectGroup(group)
    }
  }

  return (
    <div
      key={menu.name}
      className={classnames('menuItem', hasDropdown ? 'withDropdown' : '', {
        selected
      })}
      ref={setReferenceElement}
    >
      <div
        className='menuItemTarget'
        onClick={(e: React.MouseEvent) => handleMenuClick(e, menu)}
        onKeyDown={(e: React.KeyboardEvent) => handleMenuClick(e, menu)}
        role={hasDropdown ? 'button' : 'link'}
        data-testid={`menu-item-target-${menu.name}`}
        ref={dropdownRef}
        tabIndex={0}
        aria-label={`${menu.name}`}
        aria-haspopup={hasDropdown ? 'menu' : undefined}
        aria-expanded={dropdownIsOpen}
        aria-controls='menu-item-dropdown-content'
      >
        <div
          id={`${normalizeHtmlId(menu.name)}-tab`}
          className={`menuLink ${selected ? 'selected' : ''}`}
          data-testid={`menu-tab-${menu.name}`}
        >
          {menu.name}
        </div>
        {hasDropdown && (
          <ChevronRightIcon
            aria-label={t('menu-nav.view-menu-groups')}
            testId='dropdown-triangle'
            className={classnames('dropdownTriangle', { selected })}
          />
        )}
      </div>
      {hasDropdown &&
        dropdownIsOpen &&
        ReactDOM.createPortal(
          <div
            className='dropdown'
            data-testid='menu-item-dropdown'
            id='menu-item-dropdown-content'
            ref={setPopperElement}
            style={styles.popper}
            aria-labelledby={`${normalizeHtmlId(menu.name)}-tab`}
            {...attributes.popper}
            role='menu'
            tabIndex={-1}
          >
            {menu.groups.map((group) => (
              <button
                key={group.id}
                id={group.id}
                className='subMenuItem'
                onClick={() => selectGroup(group)}
                onKeyDown={(e) => handleSubMenuItemKeyDown(e, group)}
                tabIndex={0}
                role='menuitem'
              >
                {`${group.name} (${group.items.length})`}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  )
}

const MenuList = ({
  menus,
  onClickGroup,
  onClickMenu
}: {
  menus: RefMenu[]
  onClickGroup: (group: RefMenuGroup) => void
  onClickMenu: (menu: RefMenu) => void
}) => {
  const handleClick = useCallback(
    (menu: RefMenu) => {
      if (menu.groups.length === 1) {
        onClickGroup(menu.groups[0]!)
      } else {
        onClickMenu(menu)
      }
    },
    [onClickGroup, onClickMenu]
  )

  return (
    <>
      {menus.map((menu, index) => (
        <div className='section' key={menu.guid}>
          {index !== 0 && <div className='separator' />}
          <button
            className='item groupName'
            onClick={() => handleClick(menu)}
            data-testid={`mobile-menu-item-${menu.guid}`}
          >
            <span>{menu.name}</span>
            {menu.groups.length === 1 ? (
              <span data-testid='menu-group-item-count'>
                {menu.groups[0]!.items.length}
              </span>
            ) : menu.groups.length > 1 ? (
              <ChevronRightIcon
                accessibility='semantic'
                aria-label={t('menu-nav.view-menu-groups')}
              />
            ) : null}
          </button>
        </div>
      ))}
    </>
  )
}

const GroupList = ({
  groups,
  onClickGroup
}: {
  groups: RefMenuGroup[]
  onClickGroup: (group: RefMenuGroup) => void
}) => {
  return (
    <>
      {groups.map((group, index) => (
        <div className='section' key={group.guid}>
          {index !== 0 && <div className='separator' />}
          <button
            className='item groupName'
            onClick={() => onClickGroup(group)}
            data-testid={`mobile-menu-group-${group.guid}`}
          >
            <span>{group.name}</span>
            <span data-testid='menu-group-item-count'>
              {group.items.length}
            </span>
          </button>
        </div>
      ))}
    </>
  )
}

function MobileMenuModal({
  menus,
  isOpen,
  onClose,
  setSelectedGroupGuid,
  trackMobileMenuInteraction
}: {
  menus: RefMenu[]
  isOpen: boolean
  onClose: () => void
  setSelectedGroupGuid: (guid: string) => void
  trackMobileMenuInteraction: (source: 'menu_nav' | 'bottom_drawer') => void
}) {
  const { isEditor } = useEditor()
  const hasMultipleMenus = menus.length > 1

  const [selectedMenu, setSelectedMenu] = useState<RefMenu | null>(null)

  useEffect(() => {
    if (isOpen) {
      setSelectedMenu(null)
    }
  }, [isOpen])

  function handleClickGroup(group: RefMenuGroup) {
    onClose()
    scrollToRef(group.ref, isEditor)
    setSelectedGroupGuid(group.guid)
    trackMobileMenuInteraction('bottom_drawer')
  }

  return (
    <Modal onClose={onClose} isOpen={isOpen} animateClose={false}>
      <ModalOverlay fadeIn fadeOut />
      <ModalContent
        wrapperClassName='mobileMenuModalWrapper'
        contentClassName='modalContentV2 bottomDrawerMobile mobileMenuModal'
      >
        <div className='menuContent' data-testid='mobile-menu-content'>
          <div className='menusWrapper'>
            <div className='header'>
              {selectedMenu && (
                <button
                  className='headerBackButton'
                  onClick={() => setSelectedMenu(null)}
                  data-testid='mobile-menu-back'
                  aria-label={t('menu-nav.back-to-categories')}
                >
                  <ArrowBackIcon
                    accessibility='semantic'
                    aria-label={t('menu-nav.back-to-categories')}
                  />
                </button>
              )}
              <div
                className='mobileMenuHeader'
                data-testid='mobile-menu-header'
              >
                <span data-testid='mobile-menu-title'>
                  {selectedMenu ? selectedMenu.name : t('menu-nav.categories')}
                </span>
              </div>
              <button
                className='headerCloseButton'
                onClick={onClose}
                data-testid='mobile-menu-close'
                aria-label={t('menu-nav.close-menu')}
              >
                <CloseIcon
                  accessibility='semantic'
                  aria-label={t('menu-nav.close-menu')}
                />
              </button>
            </div>

            <div className='menuGroupsWrapper'>
              {!selectedMenu && hasMultipleMenus ? (
                <MenuList
                  menus={menus}
                  onClickGroup={handleClickGroup}
                  onClickMenu={setSelectedMenu}
                />
              ) : (
                <GroupList
                  groups={
                    selectedMenu ? selectedMenu.groups : menus[0]?.groups ?? []
                  }
                  onClickGroup={handleClickGroup}
                />
              )}
            </div>
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}

export default MenuNav
